// pages/BrvmPage.jsx — Espace BRVM AYA (maquette = source de vérité).
// Données réelles brvm.org uniquement — jamais de valeurs inventées.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Share2, BarChart3, Search, Bell, ArrowRight, Coins, ArrowUpRight,
  Home, Bookmark, User, Store,
} from "lucide-react";
import {
  fetchOverview, fetchStocks, fetchHistory, fetchNews,
  getWatchlist, toggleWatch, fmtNum, fmtPct, fmtCompact, deltaColor, deltaArrow, tileColor, tileInitials,
} from "@/components/brvm/brvmApi";
import BrvmChart from "@/components/brvm/BrvmChart";
import Sparkline from "@/components/brvm/Sparkline";
import StockSheet from "@/components/brvm/StockSheet";

const TABS = [
  { id: "apercu", label: "Aperçu" },
  { id: "indices", label: "Indices" },
  { id: "valeurs", label: "Valeurs" },
  { id: "actus", label: "Actualités" },
];
const PERIODS = ["1J", "1S", "1M", "1A", "5A"];

function stockSpark(s) {
  const pts = (s.spark || []).slice();
  if (pts.length < 2) {
    const today = [s.prev_close, s.open, s.close].filter((v) => Number.isFinite(v) && v > 0);
    return today.length >= 2 ? today : [];
  }
  return pts;
}

function StockRow({ s, followed, onOpen, onFollow }) {
  return (
    <div
      data-testid={`brvm-stock-row-${s.symbol}`}
      onClick={() => onOpen(s.symbol)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(s.symbol); }}
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 11px", borderRadius: 13, background: "rgba(8,38,24,0.75)", border: "1px solid rgba(16,185,129,0.22)", cursor: "pointer" }}
    >
      <span style={{ width: 36, height: 36, borderRadius: 9, background: tileColor(s.symbol), color: "#fff", fontWeight: 900, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {tileInitials(s.short_name, s.symbol)}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: "#ECF5EE", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.short_name}</div>
        <div style={{ fontSize: 11.5, color: "#9DB4A6", fontWeight: 600 }}>{fmtNum(s.close)} FCFA</div>
      </div>
      <span style={{ fontSize: 12, fontWeight: 800, color: deltaColor(s.delta_pct), flexShrink: 0 }}>{fmtPct(s.delta_pct)}</span>
      <Sparkline points={stockSpark(s)} width={64} height={28} up={s.delta_pct != null ? s.delta_pct >= 0 : null} />
      <button
        data-testid={`brvm-follow-${s.symbol}`}
        aria-label={followed ? "Ne plus suivre" : "Suivre"}
        onClick={(e) => { e.stopPropagation(); onFollow(s.symbol); }}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 4, flexShrink: 0, display: "flex" }}
      >
        <Bell size={15} color={followed ? "#F59E0B" : "#6B7F72"} fill={followed ? "#F59E0B" : "none"} />
      </button>
    </div>
  );
}

export default function BrvmPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "apercu");
  const [period, setPeriod] = useState("1J");
  const [overview, setOverview] = useState(null);
  const [failed, setFailed] = useState(false);
  const [stocks, setStocks] = useState(null);
  const [news, setNews] = useState(null);
  const [histPoints, setHistPoints] = useState(null);
  const [watch, setWatch] = useState(new Set());
  const [stockQuery, setStockQuery] = useState("");
  const [newsLimit, setNewsLimit] = useState(6);
  const sheetSymbol = params.get("symbol");

  useEffect(() => {
    fetchOverview().then((d) => (d.ok ? setOverview(d) : setFailed(true))).catch(() => setFailed(true));
    fetchStocks().then((d) => d.ok && setStocks(d.stocks)).catch(() => {});
    fetchNews().then((d) => setNews(d.articles || [])).catch(() => setNews([]));
    getWatchlist().then(setWatch);
  }, []);

  useEffect(() => {
    setHistPoints(null);
    fetchHistory("BRVM-C", period).then((d) => setHistPoints(d.points || [])).catch(() => setHistPoints([]));
  }, [period]);

  const openSheet = useCallback((symbol) => {
    params.set("symbol", symbol);
    setParams(params, { replace: false });
  }, [params, setParams]);

  const closeSheet = useCallback(() => {
    params.delete("symbol");
    setParams(params, { replace: true });
  }, [params, setParams]);

  const onFollow = async (symbol) => {
    const f = await toggleWatch(symbol);
    setWatch((prev) => { const n = new Set(prev); if (f) n.add(symbol); else n.delete(symbol); return n; });
    toast.success(f ? `${symbol} ajouté à vos valeurs suivies` : `${symbol} retiré de vos valeurs suivies`);
  };

  const share = async () => {
    const url = window.location.origin + "/brvm";
    try {
      if (navigator.share) await navigator.share({ title: "BRVM sur AYA", text: "Suivez la Bourse Régionale des Valeurs Mobilières sur AYA.", url });
      else { await navigator.clipboard.writeText(url); toast.success("Lien copié !"); }
    } catch { /* partage annulé */ }
  };

  const composite = overview?.composite;
  const updatedLabel = overview?.updated_at
    ? new Date(overview.updated_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : null;

  const filteredStocks = useMemo(() => {
    if (!stocks) return null;
    const q = stockQuery.trim().toLowerCase();
    if (!q) return stocks;
    return stocks.filter((s) => s.symbol.toLowerCase().includes(q) || s.short_name.toLowerCase().includes(q) || s.name.toLowerCase().includes(q));
  }, [stocks, stockQuery]);

  const followedStocks = useMemo(() => (stocks || []).filter((s) => watch.has(s.symbol)), [stocks, watch]);

  return (
    <div style={S.page} data-testid="brvm-page">
      <div style={S.container}>
        {/* ── HEADER ── */}
        <div style={S.topBar}>
          <button data-testid="brvm-back-btn" onClick={() => navigate(-1)} style={S.topBtn}>
            <ChevronLeft size={18} /> <span style={{ fontSize: 13, fontWeight: 700 }}>Retour</span>
          </button>
          <span style={{ fontSize: 15, fontWeight: 900, letterSpacing: 1 }}>BRVM</span>
          <button data-testid="brvm-share-btn" aria-label="Partager" onClick={share} style={{ ...S.topBtn, justifyContent: "flex-end" }}>
            <Share2 size={17} />
          </button>
        </div>

        {/* ── HERO ── */}
        <div style={{ display: "flex", gap: 13, alignItems: "flex-start", marginTop: 12 }}>
          <span style={{ display: "flex", flexShrink: 0, paddingTop: 5 }}><BarChart3 size={38} color="#F97316" strokeWidth={2.4} /></span>
          <div>
            <h1 style={{ fontSize: 34, fontWeight: 900, color: "#FFFFFF", margin: 0, lineHeight: 1, letterSpacing: -0.5 }}>BRVM</h1>
            <div style={{ fontSize: 13, color: "#CFE5D6", marginTop: 5, fontWeight: 600 }}>Bourse Régionale des Valeurs Mobilières</div>
            <div style={{ fontSize: 11.5, color: "#9DB4A6", marginTop: 1 }}>Les marchés de l'Afrique de l'Ouest</div>
          </div>
        </div>

        {/* ── ONGLETS ── */}
        <div style={{ display: "flex", gap: 8, marginTop: 16, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 2 }} data-testid="brvm-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`brvm-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              style={{
                flexShrink: 0, padding: "9px 18px", borderRadius: 12, fontSize: 12.5, fontWeight: 800, cursor: "pointer",
                border: tab === t.id ? "none" : "1px solid rgba(16,185,129,0.22)",
                background: tab === t.id ? "linear-gradient(135deg,#FB923C,#F97316)" : "rgba(8,38,24,0.75)",
                color: tab === t.id ? "#fff" : "#CFE5D6",
                boxShadow: tab === t.id ? "0 4px 14px rgba(249,115,22,0.35)" : "none",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {failed && (
          <div data-testid="brvm-unavailable" style={{ ...S.card, marginTop: 14, padding: 22, textAlign: "center", color: "#9DB4A6", fontSize: 13 }}>
            Données momentanément indisponibles. Réessayez dans quelques instants.
          </div>
        )}

        {/* ════ APERÇU ════ */}
        {tab === "apercu" && !failed && (
          <>
            <div style={{ ...S.card, marginTop: 14, padding: "13px 13px 10px" }} data-testid="brvm-composite-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 900, color: "#ECF5EE", letterSpacing: 0.6 }}>BRVM COMPOSITE</span>
                <div style={{ display: "flex", gap: 4 }}>
                  {PERIODS.map((p) => (
                    <button key={p} data-testid={`brvm-period-${p}`} onClick={() => setPeriod(p)}
                      style={{
                        padding: "4px 8px", borderRadius: 8, fontSize: 10, fontWeight: 800, cursor: "pointer",
                        border: "none",
                        background: period === p ? "#10B981" : "rgba(255,255,255,0.05)",
                        color: period === p ? "#04120B" : "#9DB4A6",
                      }}>{p}</button>
                  ))}
                </div>
              </div>
              {composite ? (
                <>
                  <div style={{ fontSize: 32, fontWeight: 900, color: "#FFFFFF", marginTop: 8, letterSpacing: -0.8 }} data-testid="brvm-composite-value">
                    {fmtNum(composite.close, 2)}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: deltaColor(composite.delta_pct), marginTop: 2 }}>
                    {composite.delta_abs != null && `${composite.delta_abs > 0 ? "+" : ""}${fmtNum(composite.delta_abs, 2)} `}
                    ({fmtPct(composite.delta_pct)}) {deltaArrow(composite.delta_pct)}
                  </div>
                  {updatedLabel && <div style={{ fontSize: 10.5, color: "#6B7F72", marginTop: 3 }}>Dernière mise à jour : {updatedLabel}</div>}
                </>
              ) : (
                <div style={{ padding: "18px 0", color: "#6B7F72", fontSize: 12 }}>Chargement…</div>
              )}
              <div style={{ marginTop: 10 }}>
                {histPoints == null
                  ? <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7F72", fontSize: 12 }}>Chargement du graphique…</div>
                  : <BrvmChart points={histPoints} period={period} />}
              </div>
            </div>

            {/* ── INDICATEURS MARCHÉ ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 10 }} data-testid="brvm-market-stats">
              <div style={{ ...S.card, padding: "9px 11px" }}>
                <div style={S.statHead}><BarChart3 size={12} color="#F97316" /> Volume</div>
                <div style={S.statVal}>{overview?.market?.volume_total != null ? fmtNum(overview.market.volume_total) : "—"}</div>
              </div>
              <div style={{ ...S.card, padding: "9px 11px" }}>
                <div style={S.statHead}><Coins size={12} color="#F59E0B" /> Valeur</div>
                <div style={S.statVal}>{overview?.market?.value_traded_fcfa != null ? `${fmtCompact(overview.market.value_traded_fcfa)} FCFA` : "—"}</div>
              </div>
              <div style={{ ...S.card, padding: "9px 11px" }}>
                <div style={S.statHead}><ArrowUpRight size={12} color="#34D399" /> Transactions</div>
                <div style={{ ...S.statVal, fontSize: 10.5, color: "#6B7F72", fontWeight: 600 }}>Donnée indisponible</div>
              </div>
            </div>

            {/* ── PRINCIPALES VALEURS ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "20px 0 10px" }}>
              <h2 style={{ fontSize: 17, fontWeight: 900, color: "#FFFFFF", margin: 0 }}>Principales valeurs</h2>
              <button data-testid="brvm-top-voir-tout" onClick={() => setTab("valeurs")} style={S.pillBtn}>
                Voir tout <ArrowRight size={13} />
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="brvm-top-stocks">
              {(overview?.top_stocks || []).map((s) => (
                <StockRow key={s.symbol} s={s} followed={watch.has(s.symbol)} onOpen={openSheet} onFollow={onFollow} />
              ))}
              {!overview && <div style={{ color: "#6B7F72", fontSize: 12, padding: "14px 0", textAlign: "center" }}>Chargement…</div>}
            </div>

            {/* ── INDICES + ACTUS ── */}
            <div className="brvm-two-col" style={{ marginTop: 14 }}>
              <div style={{ ...S.card, padding: 12 }} data-testid="brvm-mini-indices">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
                  <span style={{ fontSize: 13, fontWeight: 900, color: "#FFFFFF" }}>Indices BRVM</span>
                  <button onClick={() => setTab("indices")} style={{ ...S.pillBtn, fontSize: 10.5, padding: "4px 10px" }} data-testid="brvm-indices-voir-tout">Voir tout <ArrowRight size={11} /></button>
                </div>
                {(overview?.indices_main || []).map((i) => (
                  <div key={i.code} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid rgba(207,229,214,0.07)" }}>
                    <span style={{ fontSize: 11.5, color: "#CFE5D6", fontWeight: 700 }}>{i.name}</span>
                    <span style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                      <span style={{ fontSize: 11.5, fontWeight: 800, color: "#ECF5EE" }}>{fmtNum(i.close, 2)}</span>
                      <span style={{ fontSize: 10.5, fontWeight: 800, color: deltaColor(i.delta_pct) }}>{fmtPct(i.delta_pct)} {deltaArrow(i.delta_pct)}</span>
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ ...S.card, padding: 12 }} data-testid="brvm-mini-news">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
                  <span style={{ fontSize: 13, fontWeight: 900, color: "#FFFFFF" }}>Dernières actualités</span>
                  <button onClick={() => setTab("actus")} style={{ ...S.pillBtn, fontSize: 10.5, padding: "4px 10px" }} data-testid="brvm-news-voir-tout">Voir tout <ArrowRight size={11} /></button>
                </div>
                {news == null && <div style={{ color: "#6B7F72", fontSize: 11.5 }}>Chargement…</div>}
                {news != null && news.length === 0 && <div style={{ color: "#6B7F72", fontSize: 11.5 }}>Actualités momentanément indisponibles.</div>}
                {(news || []).slice(0, 3).map((a) => (
                  <a key={a.id} href={a.url} target="_blank" rel="noreferrer" style={{ display: "flex", gap: 9, padding: "6px 0", textDecoration: "none", alignItems: "center" }}>
                    {a.image && <img src={a.image} alt="" loading="lazy" style={{ width: 44, height: 36, objectFit: "cover", borderRadius: 7, flexShrink: 0 }} />}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, color: "#ECF5EE", fontWeight: 700, lineHeight: 1.3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{a.title}</div>
                      {a.age_label && <div style={{ fontSize: 9.5, color: "#6B7F72", marginTop: 2 }}>{a.age_label}</div>}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ════ INDICES ════ */}
        {tab === "indices" && !failed && (
          <div style={{ marginTop: 14 }} data-testid="brvm-indices-tab">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(165px, 1fr))", gap: 9 }}>
              {(overview?.indices_all || []).map((i) => (
                <div key={i.code} style={{ ...S.card, padding: "11px 12px" }} data-testid={`brvm-index-card-${i.code}`}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#CFE5D6" }}>{i.name}</div>
                  <div style={{ fontSize: 19, fontWeight: 900, color: "#FFFFFF", marginTop: 4 }}>{fmtNum(i.close, 2)}</div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: deltaColor(i.delta_pct), marginTop: 2 }}>
                    {fmtPct(i.delta_pct)} {deltaArrow(i.delta_pct)}
                  </div>
                  {i.ytd_pct != null && <div style={{ fontSize: 9.5, color: "#6B7F72", marginTop: 3 }}>Depuis le 31 déc. : {fmtPct(i.ytd_pct)}</div>}
                </div>
              ))}
              {!overview && <div style={{ color: "#6B7F72", fontSize: 12 }}>Chargement…</div>}
            </div>
          </div>
        )}

        {/* ════ VALEURS ════ */}
        {tab === "valeurs" && !failed && (
          <div style={{ marginTop: 14 }} data-testid="brvm-valeurs-tab">
            <div style={{ display: "flex", alignItems: "center", gap: 8, height: 42, padding: "0 13px", borderRadius: 999, background: "rgba(6,34,22,0.9)", border: "1.5px solid rgba(16,185,129,0.4)" }}>
              <Search size={14} color="#9DB4A6" />
              <input
                data-testid="brvm-stock-search"
                value={stockQuery}
                onChange={(e) => setStockQuery(e.target.value)}
                placeholder="Rechercher une valeur…"
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "#ECF5EE", fontSize: 13 }}
              />
            </div>
            {followedStocks.length > 0 && !stockQuery && (
              <>
                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1, color: "#F59E0B", textTransform: "uppercase", margin: "14px 2px 7px" }}>⭐ Valeurs suivies</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="brvm-followed-list">
                  {followedStocks.map((s) => (
                    <StockRow key={s.symbol} s={s} followed onOpen={openSheet} onFollow={onFollow} />
                  ))}
                </div>
              </>
            )}
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1, color: "#6B7F72", textTransform: "uppercase", margin: "14px 2px 7px" }}>
              {stockQuery ? "Résultats" : "Toutes les valeurs"}{filteredStocks ? ` · ${filteredStocks.length}` : ""}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="brvm-stocks-list">
              {filteredStocks == null && <div style={{ color: "#6B7F72", fontSize: 12, textAlign: "center", padding: "14px 0" }}>Chargement…</div>}
              {filteredStocks != null && filteredStocks.length === 0 && <div style={{ color: "#6B7F72", fontSize: 12, textAlign: "center", padding: "14px 0" }}>Aucune valeur ne correspond.</div>}
              {(filteredStocks || []).map((s) => (
                <StockRow key={s.symbol} s={s} followed={watch.has(s.symbol)} onOpen={openSheet} onFollow={onFollow} />
              ))}
            </div>
          </div>
        )}

        {/* ════ ACTUALITÉS ════ */}
        {tab === "actus" && (
          <div style={{ marginTop: 14 }} data-testid="brvm-actus-tab">
            {news == null && <div style={{ color: "#6B7F72", fontSize: 12, textAlign: "center", padding: "18px 0" }}>Chargement…</div>}
            {news != null && news.length === 0 && (
              <div style={{ ...S.card, padding: 20, textAlign: "center", color: "#9DB4A6", fontSize: 12.5 }}>Actualités momentanément indisponibles.</div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {(news || []).slice(0, newsLimit).map((a) => (
                <a key={a.id} href={a.url} target="_blank" rel="noreferrer" data-testid={`brvm-news-${a.id}`} style={{ ...S.card, display: "flex", gap: 11, padding: 10, textDecoration: "none", alignItems: "center" }}>
                  {a.image && <img src={a.image} alt="" loading="lazy" style={{ width: 86, height: 64, objectFit: "cover", borderRadius: 9, flexShrink: 0 }} />}
                  <div style={{ minWidth: 0 }}>
                    <span style={{ display: "inline-block", padding: "1.5px 7px", borderRadius: 999, background: "rgba(249,115,22,0.15)", color: "#F97316", fontSize: 8.5, fontWeight: 900, letterSpacing: 0.6 }}>BRVM</span>
                    <div style={{ fontSize: 12.5, color: "#ECF5EE", fontWeight: 700, lineHeight: 1.3, marginTop: 4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{a.title}</div>
                    <div style={{ fontSize: 10, color: "#6B7F72", marginTop: 3 }}>{[a.source, a.age_label].filter(Boolean).join(" · ")}</div>
                  </div>
                </a>
              ))}
            </div>
            {news && newsLimit < news.length && (
              <button data-testid="brvm-news-more" onClick={() => setNewsLimit((n) => n + 6)} style={{ ...S.pillBtn, margin: "12px auto 0", display: "flex" }}>
                Voir tout <ArrowRight size={13} />
              </button>
            )}
          </div>
        )}
      </div>

      {sheetSymbol && (
        <StockSheet
          symbol={sheetSymbol}
          followed={watch.has(sheetSymbol)}
          onToggleFollow={(sym, f) => setWatch((prev) => { const n = new Set(prev); if (f) n.add(sym); else n.delete(sym); return n; })}
          onClose={closeSheet}
        />
      )}

      {/* ── NAV BASSE AYA ── */}
      <nav style={S.bottomNav} data-testid="brvm-bottom-nav">
        {[
          { Icon: Home, label: "Accueil", go: () => navigate("/search") },
          { Icon: Search, label: "Rechercher", go: () => navigate("/search") },
          { Icon: Store, label: "Kiosque", kiosque: true, go: () => navigate("/kiosque") },
          { Icon: Bookmark, label: "Enregistrés", go: () => navigate("/enregistres") },
          { Icon: User, label: "Profil", go: () => navigate("/mon-compte") },
        ].map((t) => t.kiosque ? (
          <button key={t.label} onClick={t.go} aria-label="Kiosque" style={{ ...S.navItem, padding: "0 12px", marginTop: -16, color: "#F97316" }}>
            <span style={S.kiosqueCircle}><t.Icon size={19} color="#fff" strokeWidth={2.2} /></span>
            <span style={{ fontSize: 10, fontWeight: 700 }}>{t.label}</span>
          </button>
        ) : (
          <button key={t.label} onClick={t.go} aria-label={t.label} style={S.navItem}>
            <t.Icon size={19} strokeWidth={2} />
            <span style={{ fontSize: 10, fontWeight: 600 }}>{t.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

const S = {
  page: { minHeight: "100vh", background: "radial-gradient(ellipse at top, #072115 0%, #04120B 60%, #030B07 100%)", color: "#ECF5EE", fontFamily: "inherit", paddingBottom: 86 },
  container: { maxWidth: 880, margin: "0 auto", padding: "10px 14px 0" },
  topBar: { display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center" },
  topBtn: { display: "flex", alignItems: "center", gap: 2, background: "none", border: "none", color: "#CFE5D6", cursor: "pointer", padding: 4 },
  card: { borderRadius: 14, background: "rgba(8,38,24,0.75)", border: "1px solid rgba(16,185,129,0.22)" },
  statHead: { display: "flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 800, color: "#9DB4A6", textTransform: "uppercase", letterSpacing: 0.4 },
  statVal: { fontSize: 13.5, fontWeight: 900, color: "#FFFFFF", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  pillBtn: { display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 13px", borderRadius: 999, border: "1px solid rgba(16,185,129,0.45)", background: "rgba(16,185,129,0.12)", color: "#34D399", fontSize: 11.5, fontWeight: 800, cursor: "pointer" },
  bottomNav: { position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 60, display: "flex", justifyContent: "space-around", padding: "5px 4px calc(6px + env(safe-area-inset-bottom))", background: "rgba(4,18,11,0.96)", backdropFilter: "blur(16px)", borderTop: "1px solid rgba(16,185,129,0.22)" },
  navItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 2, background: "none", border: "none", color: "#7C9384", cursor: "pointer", padding: "5px 12px", borderRadius: 12 },
  kiosqueCircle: { width: 42, height: 42, borderRadius: "50%", background: "linear-gradient(135deg,#FB923C,#EA580C)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(249,115,22,0.5)", border: "2.5px solid rgba(4,18,11,0.96)" },
};
